import { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Lottie from 'react-lottie';
import anim from 'assets/lottie/team/team_06.json';

const Contact = () => {
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState({ type: '', message: '' });

    const animationOptions = {
        loop: true,
        autoplay: true,
        animationData: anim,
        rendererSettings: {
            preserveAspectRatio: 'xMidYMid meet',
        },
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setFeedback({ type: '', message: '' });

        const { name, email, message } = formData;

        if (!name || !email || !message) {
            setFeedback({ type: 'error', message: 'All fields are required.' });
            setLoading(false);
            return;
        }

        try {
            const sendMail = httpsCallable(getFunctions(), 'sendMail');
            await sendMail({ name, email, msg: message });
            window.dataLayer.push({ event: 'lead-form-submit' });
            setFeedback({ type: 'success', message: 'Message sent successfully!' });
            setFormData({ name: '', email: '', message: '' });
        } catch (error) {
            console.error(error);
            setFeedback({ type: 'error', message: 'Failed to send your message. Please try again later.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full bg-gradient-to-r from-purple-600 to-indigo-500 text-white pt-5 pb-20 px-4 flex flex-col items-center">
            {/* Title and Email with Animation */}
            <div className="w-full flex flex-col lg:flex-row items-center justify-center gap-0 lg:gap-0 mb-10 lg:mb-0">
                {/* Animation */}
                <div className="w-[80%] lg:w-[30%] lg:max-w-none">
                    <Lottie
                        options={animationOptions}
                        style={{ width: '100%', height: '100%', cursor: 'default' }}
                        isClickToPauseDisabled={true}
                    />
                </div>

                {/* Title and Email */}
                <div className="text-center lg:text-left">
                    <h1 className="text-4xl md:text-5xl font-title font-extrabold mb-2">
                        Let's Chat
                    </h1>
                    <p className="text-lg font-body">
                        Email us at{' '}
                        <a
                            href="mailto:hello@bitbit.studio"
                            className="text-yellow-400 underline"
                        >
                            hello@bitbit.studio
                        </a>
                        <br />
                        Or use the form below.
                    </p>
                </div>
            </div>

            {/* Contact Form */}
            <div className="w-full md:w-[70%] lg:w-[50%] bg-gray-800 text-white rounded-2xl shadow-lg p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name Field */}
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-600"
                        placeholder="Your Name"
                        required
                        disabled={loading}
                    />

                    {/* Email Field */}
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 rounded-lg border-2 border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-600"
                        placeholder="Your Email"
                        required
                        disabled={loading}
                    />

                    {/* Message Field */}
                    <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows="5"
                        className="w-full px-4 py-3 rounded-lg border-2 border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-600"
                        placeholder="What's on your mind? :)"
                        required
                        disabled={loading}
                    />

                    {/* Feedback */}
                    {feedback.message && (
                        <p
                            className={`text-center text-lg font-title ${
                                feedback.type === 'error' ? 'text-red-500' : 'text-green-500'
                            }`}
                        >
                            {feedback.message}
                        </p>
                    )}

                    {/* Submit Button */}
                    <div className="flex justify-center items-center">
                        <button
                            type="submit"
                            className="w-full flex justify-center items-center btn btn-primary px-8 py-3 text-lg rounded-full shadow-md"
                            disabled={loading}
                        >
                            {loading ? 'Sending...' : 'Send Message'}         
                        </button>
                        {loading && <div className="absolute w-10 loading"/>}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Contact;
